import { AlarmProfiles } from "./alarm-profiles";
import { AlarmProfile } from "./alarm-profile";
import { Alarm, AlarmType, AlarmTypeValue } from "./alarm";
import { Criteria } from "./criteria";
import { EMail } from "./email";
import { Notifications } from "./notifications";
import { Condition } from "./condition";
import { v4 as uuidv4 } from "uuid";
import { OurNode } from "@src/cloud-connect/poc/our-node";

class ParseAlarmProfilesDto {
  response: any;
  getNodeById: (id: string) => any;
  OurNodeClass: new (params: any) => any;
  source_id: string;
  client_id: string;
}

export const parseAlarmProfiles = (
  dto: ParseAlarmProfilesDto,
): AlarmProfiles => {
  if (dto.response.length === 0) {
    // If response is empty, return a default alarm profile
    const newEMail = new EMail(false, "", "", []);
    const newNotifications = new Notifications(newEMail);

    const defaultAlarmProfile = new AlarmProfile(
      dto.source_id,
      dto.client_id,
      true,
      uuidv4(),
      "Default Alarm Profile",
      "Default Alarm Profile Description",
      newNotifications,
      [],
      false,
    );

    return new AlarmProfiles({ alarmProfiles: [defaultAlarmProfile] });
  }

  const alarmProfiles = dto.response.map((profileData: any) => {
    const emailData = profileData.notifications.email;
    const email = new EMail(
      emailData.active,
      emailData.subject_template,
      emailData.body_template,
      emailData.recipients,
    );

    const notifications = new Notifications(email);

    const activeAlarms = Object.entries(profileData.active_alarms || {}).map(
      ([_, alarmData]: [string, any]) => {
        return createAlarmFromData(
          alarmData,
          dto.getNodeById(alarmData.node.id),
          dto.OurNodeClass,
          alarmData.type || AlarmTypeValue.SENSOR,
        );
      },
    );

    const inactiveAlarms = Object.entries(
      profileData.inactive_alarms || {},
    ).map(([_, alarmData]: [string, any]) => {
      return createAlarmFromData(
        alarmData,
        dto.getNodeById(alarmData.node.id),
        dto.OurNodeClass,
        alarmData.type || AlarmTypeValue.SENSOR,
      );
    });

    const kpiAlarms = Object.entries(profileData.kpi_alarms || {}).map(
      ([_, alarmData]: [string, any]) => {
        return createAlarmFromData(
          alarmData,
          null,
          dto.OurNodeClass,
          alarmData.type || AlarmTypeValue.COMPUTED,
        );
      },
    );
    const allAlarms = [...kpiAlarms, ...activeAlarms, ...inactiveAlarms];

    return new AlarmProfile(
      profileData.source_id,
      profileData.client_id || "",
      profileData.active,
      profileData.id,
      profileData.name,
      profileData.description,
      notifications,
      allAlarms,
      false,
    );
  });

  return new AlarmProfiles({ alarmProfiles });
};

const createAlarmFromData = (
  alarmData: any,
  node: OurNode | null,
  OurNodeClass: new (params: any) => any,
  type: AlarmType = AlarmTypeValue.SENSOR,
): Alarm => {
  // const node = getNodeById(alarmData.node.id);

  let ourNode;

  if (node) {
    // If node is found, create it with the full data
    ourNode = new OurNodeClass({
      id: alarmData.node.id,
      name: alarmData.node.name,
      key: node.key,
      node_type: node.node_type,
      attributes: node.attributes,
      source_type: node.source_type,
      selected: false,
      visible: true,
      collapsed: false,
      transfered: true,
      neighborAbove: node.neighborAbove,
      neighborBelow: node.neighborBelow,
      parent: node.parent,
      updateChildren: node.updateChildren,
    });
  } else {
    // If node is not found, create a new node with minimal data
    ourNode = new OurNodeClass({
      id: alarmData.node.id,
      name: alarmData.node.name,
      key: null,
      node_type: null,
      attributes: {
        data_type: null,
        unit: {
          code: 0,
          display_name: alarmData.node.unit,
          description: "",
        },
        last_known_value: null,
        last_known_value_timestamp: null,
        data_type_as_string: "",
        node_class_as_string: "",
      },
      source_type: null,
      selected: false,
      visible: true,
      collapsed: false,
      transfered: true,
      neighborAbove: null,
      neighborBelow: null,
      parent: null,
      updateChildren: null,
    });
  }

  let conditions;
  let existingConditions;

  if (alarmData.criteria?.conditions?.[0]?.user_input === "number") {
    existingConditions = alarmData.criteria.conditions;

    if (existingConditions.length === 0) {
      // If there are no conditions, add both less-than and greater-than
      conditions = [
        new Condition("", false, "less-than"),
        new Condition("", false, "greater-than"),
      ];
    } else if (existingConditions.length === 1) {
      // If one condition exists, check if it's less-than or greater-than
      const firstCondition = existingConditions[0];

      if (
        firstCondition.operator === "less-than" ||
        firstCondition.operator === "less-than-or-equal"
      ) {
        conditions = [
          new Condition(
            firstCondition.user_input || "",
            firstCondition.case_sensitive || false,
            firstCondition.operator || "",
          ),
          new Condition("", false, "greater-than"),
        ];
      } else if (
        firstCondition.operator === "greater-than" ||
        firstCondition.operator === "greater-than-or-equal"
      ) {
        conditions = [
          new Condition("", false, "less-than"),
          new Condition(
            firstCondition.user_input || "",
            firstCondition.case_sensitive || false,
            firstCondition.operator || "",
          ),
        ];
      }
    } else {
      // If there are already multiple conditions, just map over them
      conditions = existingConditions.map(
        (conditionData: any) =>
          new Condition(
            conditionData.user_input,
            conditionData.case_sensitive,
            conditionData.operator,
          ),
      );
    }
  } else {
    existingConditions = alarmData.criteria.conditions;
    conditions = existingConditions.map(
      (conditionData: any) =>
        new Condition(
          conditionData.user_input,
          conditionData.case_sensitive,
          conditionData.operator,
        ),
    );
  }

  // const criteria = new Criteria(alarmData.criteria.join_operator, conditions);
  const criteria = new Criteria("or", conditions);

  return new Alarm(
    ourNode,
    criteria,
    alarmData.active,
    type,
    alarmData.data_type,
  );
};
